import React, {
  memo,
  useState,
  Suspense,
  lazy,
  useCallback,
  useEffect,
} from 'react';
import {
  Handle,
  Position,
  NodeToolbar,
  useNodeId,
  useReactFlow,
  NodeResizer,
} from '@xyflow/react';

// lazy picker
const Picker = lazy(() => import('@emoji-mart/react'));
// ✅ the actual emoji dataset:
import emojiData from '@emoji-mart/data';

const ToolbarNode = ({ data, selected }) => {
  const [open, setOpen] = useState(false);
  const id = useNodeId();
  const { setNodes } = useReactFlow();

  const [label, setLabel] = useState(data.label ?? '');
  const [desc, setDesc] = useState(data.description ?? '');

  useEffect(() => setLabel(data.label ?? ''), [data.label]);
  useEffect(() => setDesc(data.description ?? ''), [data.description]);

   const handleResize = useCallback(
    (_evt, params) => {
      if (!id) return;
      const { width, height } = params || {};
      if (width == null && height == null) return;

      setNodes((ns) =>
        ns.map((n) =>
          n.id === id
            ? {
                ...n,
                style: {
                  ...(n.style || {}),
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
                // also stash in data so your save routine (which only sends data) will persist it
                data: {
                  ...n.data,
                  ...(width != null ? { width } : {}),
                  ...(height != null ? { height } : {}),
                },
              }
            : n
        )
      );
    },
    [id, setNodes]
  );

  const commit = useCallback(
    (patch) => {
      if (!id) return;
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, ...patch } } : n
        )
      );
    },
    [id, setNodes]
  );

  const stopKeys = (e) => e.stopPropagation();

  const emoji = data.emoji ?? '🚀';
  const setEmoji = (val) => commit({ emoji: val });

  return (
    <div
      className="rounded-lg border"
      style={{
        background: data.bgColor ?? '#ffffff',
        color: data.fontColor ?? '#111111',
        fontSize: (data.fontSize ?? 14) + 'px',
        padding: 12,
        width: data.width ?? 'auto',
        height: data.height ?? 'auto'
      }}
    >
      <NodeToolbar
        isVisible={selected}
        position={Position.Top}
        className="rounded-xl border bg-white p-2 shadow z-50"
      >
        <button
          className="px-2 py-1 rounded border"
          onClick={() => setOpen((v) => !v)}
          aria-label="Pick emoji"
          title="Pick emoji"
        >
          {emoji} ▼
        </button>

        {open && (
          <div className="relative">
            <div className="absolute mt-2 z-[9999]">
              <Suspense fallback={<div className="p-3 text-sm">Loading…</div>}>
                {/* ✅ pass the emoji-mart dataset, not data */}
                <Picker
                  data={emojiData}
                  onEmojiSelect={(e) => {
                    setEmoji(e.native);
                    setOpen(false);
                  }}
                  previewPosition="none"
                  searchPosition="top"
                  navPosition="bottom"
                  perLine={8}
                  emojiSize={22}
                  theme="auto"
                />
              </Suspense>
            </div>
          </div>
        )}
      </NodeToolbar>

      <NodeResizer
        isVisible={selected}
        minWidth={140}
        minHeight={100}
        onResizeEnd={handleResize}
        onResize={handleResize}
      />

      <div
        className="flex items-center gap-2 p-2 rounded-lg border bg-white/90"
        style={{
          height: '100%',
        }}
      >
        <span style={{ fontSize: 28, lineHeight: 1 }}>{emoji}</span>
        <div>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => commit({ label })}
            onKeyDown={stopKeys}
            className="node-label"
            style={{
              width: '100%',
              border: 'none',
              background: 'transparent',
              color: data.fontColor ?? '#111111',
              fontSize: (data.fontSize ?? 14) + 'px',
              textAlign: 'center',
            }}
          />
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            onBlur={() => commit({ description: desc })}
            onKeyDown={stopKeys}
            style={{
              width: '100%',
              border: 'none',
              background: 'transparent',
              color: data.fontColor ?? '#352a2a',
              fontSize: (data.fontSize ?? 14) - 2 + 'px',
              resize: 'none',
              minHeight: '40px',
              textAlign: 'center',
            }}
          />
        </div>
      </div>

        <Handle
          type="source"
          position={Position.Left}
          id="1"
          className="custom-handle-source"
          style={{ left: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="1"
          className="custom-handle-target"
          style={{ left: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
  
        <Handle
          type="source"
          position={Position.Right}
          id="2"
          className="custom-handle-source"
          style={{ right: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
        <Handle
          type="target"
          position={Position.Right}
          id="2"
          className="custom-handle-target"
          style={{ right: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
  
        <Handle
          type="source"
          position={Position.Top}
          id="4"
          className="custom-handle-source"
          style={{ top: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
        <Handle
          type="target"
          position={Position.Top}
          id="4"
          className="custom-handle-target"
          style={{ top: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
  
        <Handle
          type="source"
          position={Position.Bottom}
          id="3"
          className="custom-handle-source"
          style={{ bottom: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="3"
          className="custom-handle-target"
          style={{ bottom: -6 , visibility: selected ? 'visible' : 'hidden'}}
        />
    </div>
  );
};

export default memo(ToolbarNode);
